import { useState, useCallback, useEffect, useRef } from 'react';
import { Bot, MessageCircle, MessageSquare, Phone, X, Sparkles } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { useChatContext, type ChatConversation } from '@/contexts/ChatContext';
import { PersonalAgentChat } from '@/components/ai/PersonalAgentChat';
import { CaseChatPopup } from '@/components/office/CaseChatPopup';
import { useSoftphoneContextSafe } from '@/contexts/SoftphoneContext';
import { type LastCallInfo } from '@/hooks/useSoftphone';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { usePanelPreferences } from '@/hooks/usePanelPreferences';
import { CommunicationPanelContainer } from './CommunicationPanelContainer';
import { FloatingPanel } from './FloatingPanel';
import { InternalChatPanel } from './InternalChatPanel';
import { EmbeddedSoftphoneContent } from '@/components/softphone/SoftphoneWidget';
import { useInternalChat } from '@/hooks/useInternalChat';
import type { PanelState, PanelKey, CommunicationPanelStates } from '@/types/communication-panels';

type AgentMode = 'office' | 'field' | 'portal';

interface UnifiedChatBubbleProps {
  mode?: AgentMode;
  companyName?: string;
}

export function UnifiedChatBubble({ mode = 'office', companyName }: UnifiedChatBubbleProps) {
  const isMobile = useIsMobile();
  const location = useLocation();
  const [isDesktop, setIsDesktop] = useState<boolean>(() =>
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 1024px)').matches : true
  );
  useEffect(() => {
    const mql = window.matchMedia('(min-width: 1024px)');
    const onChange = () => setIsDesktop(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  const [menuOpen, setMenuOpen] = useState(false);
  const [chatView, setChatView] = useState(false);
  const [callSummaryPrompt, setCallSummaryPrompt] = useState<string | null>(null);
  const [pendingCallSid, setPendingCallSid] = useState<string | null>(null);
  const summaryPollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { user } = useAuth();

  const { states, poppedOut, positions, setPanelState, popOut, dock, updatePosition, loaded } = usePanelPreferences();
  const { totalUnread: internalChatUnread } = useInternalChat();

  // Z-index for popped out panels
  const [zIndices, setZIndices] = useState({ ai_assistent: 50, omstilling: 51, intern_chat: 52 });
  const bringToFront = useCallback((key: PanelKey) => {
    setZIndices(prev => {
      const max = Math.max(...Object.values(prev));
      return { ...prev, [key]: max + 1 };
    });
  }, []);

  const chatCtx = mode === 'office' ? useChatContextSafe() : null;
  const conversations = chatCtx?.conversations || [];
  const hasConversations = conversations.length > 0;

  const phone = mode === 'office' ? useSoftphoneContextSafe() : null;
  const phoneStatus = phone?.status ?? 'offline';
  const lastCallInfo = phone?.lastCallInfo ?? null;

  const phoneActive = phoneStatus === 'on-call' || phoneStatus === 'consulting' || phoneStatus === 'ringing';
  const phoneRinging = phoneStatus === 'ringing';

  const anyPanelVisible = states.omstilling !== 'closed' || states.ai_assistent !== 'closed' || states.intern_chat !== 'closed';

  // Container states = only non-popped panels
  const containerStates: CommunicationPanelStates = {
    omstilling: poppedOut.omstilling ? 'closed' : states.omstilling,
    ai_assistent: poppedOut.ai_assistent ? 'closed' : states.ai_assistent,
    intern_chat: poppedOut.intern_chat ? 'closed' : states.intern_chat,
  };

  // Listen for toggle-softphone event
  useEffect(() => {
    const handleToggle = () => {
      setPanelState('omstilling', 'open');
    };
    window.addEventListener('toggle-softphone', handleToggle);
    return () => window.removeEventListener('toggle-softphone', handleToggle);
  }, [setPanelState]);

  // Auto-open on incoming call
  useEffect(() => {
    if (phoneStatus === 'ringing' || phoneStatus === 'consulting') {
      setPanelState('omstilling', 'open');
    }
  }, [phoneStatus, setPanelState]);

  // When call ends and we have lastCallInfo, poll for AI summary
  useEffect(() => {
    if (!lastCallInfo?.callSid) return;
    const callSid = lastCallInfo.callSid;
    setPendingCallSid(callSid);
    setPanelState('ai_assistent', 'open');

    let attempts = 0;
    const maxAttempts = 60;
    summaryPollingRef.current = setInterval(async () => {
      attempts++;
      if (attempts > maxAttempts) {
        if (summaryPollingRef.current) clearInterval(summaryPollingRef.current);
        return;
      }
      try {
        const { data } = await (supabase as any)
          .from('switchboard_calls')
          .select('ai_summary, ivr_selection')
          .eq('twilio_call_sid', callSid)
          .maybeSingle();
        if (data?.ai_summary) {
          if (summaryPollingRef.current) clearInterval(summaryPollingRef.current);
          setCallSummaryPrompt(data.ai_summary);
        }
      } catch { /* ignore */ }
    }, 5000);

    const dirLabel = lastCallInfo.direction === 'inbound' ? 'indgående' : 'udgående';
    const durationMm = Math.floor((lastCallInfo.duration || 0) / 60);
    const durationSs = (lastCallInfo.duration || 0) % 60;
    setCallSummaryPrompt(`Opkald afsluttet. Venter på AI-transskription og resumé...\n\n**Opkaldsinfo:**\n- Retning: ${dirLabel}\n- Varighed: ${durationMm}m ${durationSs}s\n- Nummer: ${lastCallInfo.callerNumber || 'Ukendt'}`);

    phone?.clearLastCallInfo?.();

    return () => {
      if (summaryPollingRef.current) clearInterval(summaryPollingRef.current);
    };
  }, [lastCallInfo?.callSid]);

  // Save AI summary as call note
  const handleSaveNote = useCallback(async (noteText: string) => {
    if (!pendingCallSid || !user?.company_id) {
      toast.error('Ingen opkaldsdata tilgængelig');
      return;
    }
    try {
      await (supabase as any)
        .from('switchboard_calls')
        .update({ notes: noteText })
        .eq('twilio_call_sid', pendingCallSid);

      const { data: callData } = await (supabase as any)
        .from('switchboard_calls')
        .select('customer_id, customers(name), direction, caller_number, callee_number, duration_seconds')
        .eq('twilio_call_sid', pendingCallSid)
        .maybeSingle();

      if (callData?.customer_id) {
        const { data: project } = await supabase
          .from('projects')
          .select('id')
          .eq('customer_id', callData.customer_id)
          .eq('company_id', user.company_id)
          .is('deleted_at', null)
          .not('status', 'in', '(completed,cancelled)')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        const phoneNum = callData.direction === 'inbound' ? callData.caller_number : callData.callee_number;
        await supabase.from('activity_logs').insert({
          company_id: user.company_id,
          user_id: user.id,
          entity_type: 'project',
          entity_id: project?.id || callData.customer_id,
          action: 'call_note',
          metadata: {
            description: noteText.substring(0, 500),
            phone: phoneNum,
            direction: callData.direction,
            customer_name: (callData as any).customers?.name ?? null,
            duration_seconds: callData.duration_seconds,
            source: 'ai_summary',
          },
        });
      }
      toast.success('Opkaldsnote gemt');
      setPendingCallSid(null);
      setCallSummaryPrompt(null);
    } catch {
      toast.error('Kunne ikke gemme note');
    }
  }, [pendingCallSid, user]);

  // Hide on public pages
  const isPublicPage = location.pathname === '/' || location.pathname === '/demo';
  if (isPublicPage) return null;

  // Skjul flydende bubble på mobil og iPad — kun desktop (≥1024px)
  if (!isDesktop) return null;

  if (mode !== 'office') {
    return <PersonalAgentChat mode={mode} companyName={companyName} />;
  }

  // State indicator
  const getStateIndicator = (panelState: PanelState, isPoppedOut: boolean) => {
    if (panelState === 'closed') return null;
    if (isPoppedOut) return <span className="w-2 h-2 rounded-full bg-blue-500 shrink-0" title="Popped ud" />;
    if (panelState === 'open') return <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />;
    if (panelState === 'minimized') return <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0" />;
    return null;
  };

  const handleTogglePanel = (key: PanelKey) => {
    if (states[key] === 'closed') {
      if (poppedOut[key]) {
        dock(key);
      }
      setPanelState(key, 'open');
    } else if (states[key] === 'minimized' && !poppedOut[key]) {
      setPanelState(key, 'open');
    } else {
      setPanelState(key, 'closed');
    }
    setMenuOpen(false);
  };

  const handlePopOut = (key: PanelKey) => {
    popOut(key);
  };

  const handleDock = (key: PanelKey) => {
    dock(key);
  };

  const handleOpenConversation = (conv: ChatConversation) => {
    if (chatCtx) {
      chatCtx.setActiveConversation(conv.customerId);
      chatCtx.setIsOpen(true);
      chatCtx.setIsMinimized(false);
    }
    setChatView(true);
    setMenuOpen(false);
  };

  function renderFloatingContent(key: PanelKey) {
    switch (key) {
      case 'omstilling':
        return <EmbeddedSoftphoneContent onBack={() => setPanelState('omstilling', 'closed')} />;
      case 'ai_assistent':
        return (
          <PersonalAgentChat
            mode="office"
            embedded
            compact
            initialMessage={callSummaryPrompt || undefined}
            onSaveNote={pendingCallSid ? handleSaveNote : undefined}
            showSaveNote={!!pendingCallSid}
          />
        );
      case 'intern_chat':
        return <InternalChatPanel embedded />;
    }
  }

  const panelIcons: Record<PanelKey, React.ReactNode> = {
    omstilling: <Phone className="w-4 h-4 text-emerald-500" />,
    ai_assistent: <Bot className="w-4 h-4 text-primary" />,
    intern_chat: <MessageSquare className="w-4 h-4 text-blue-500" />,
  };

  const panelTitles: Record<PanelKey, string> = {
    omstilling: 'Omstilling',
    ai_assistent: 'AI Assistent',
    intern_chat: 'Intern Chat',
  };

  // Chat view
  if (chatView) {
    return (
      <>
        <CaseChatPopup onBack={() => setChatView(false)} />
        <CommunicationPanelContainer
          states={containerStates}
          onStateChange={setPanelState}
          onPopOut={handlePopOut}
          phoneStatus={phoneStatus}
          callSummaryPrompt={callSummaryPrompt}
          pendingCallSid={pendingCallSid}
          handleSaveNote={handleSaveNote}
        />
        {renderPoppedPanels()}
      </>
    );
  }

  return (
    <>
      {/* Docked container */}
      <CommunicationPanelContainer
        states={containerStates}
        onStateChange={setPanelState}
        onPopOut={handlePopOut}
        phoneStatus={phoneStatus}
        callSummaryPrompt={callSummaryPrompt}
        pendingCallSid={pendingCallSid}
        handleSaveNote={handleSaveNote}
      />

      {/* Popped out floating panels */}
      {renderPoppedPanels()}

      {/* Floating bubble */}
      {!menuOpen && (
        <button
          onClick={() => setMenuOpen(true)}
          className={cn(
            "fixed z-50 rounded-full shadow-lg hover:scale-105 transition-transform flex items-center justify-center",
            phoneActive
              ? phoneStatus === 'ringing'
                ? "bg-destructive text-destructive-foreground animate-pulse"
                : phoneStatus === 'consulting'
                  ? "bg-purple-500 text-white"
                  : "bg-blue-500 text-white"
              : "bg-primary text-primary-foreground",
            isMobile ? "bottom-20 right-4 w-12 h-12" : "bottom-6 right-6 w-14 h-14"
          )}
          title="Åbn assistent"
        >
          {phoneActive ? (
            <Phone className={cn(isMobile ? "w-5 h-5" : "w-6 h-6")} />
          ) : (
            <Sparkles className={cn(isMobile ? "w-5 h-5" : "w-6 h-6")} />
          )}
          {(hasConversations || phoneActive || anyPanelVisible || internalChatUnread > 0) && (
            <span className={cn(
              "absolute -top-1 -right-1 w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center",
              phoneActive
                ? "bg-destructive text-destructive-foreground"
                : internalChatUnread > 0
                  ? "bg-primary text-primary-foreground"
                  : "bg-amber-500 text-white"
            )}>
              {phoneActive ? '!' : internalChatUnread > 0 ? internalChatUnread : '⚡'}
            </span>
          )}
        </button>
      )}

      {/* Menu */}
      {menuOpen && (
        <div className="fixed bottom-4 right-4 z-[999] w-[320px] max-w-[calc(100vw-2rem)] bg-background border border-border rounded-xl shadow-2xl overflow-hidden animate-scale-in">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-primary/5">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-foreground">Kommunikation</span>
            </div>
            <button onClick={() => setMenuOpen(false)} className="w-7 h-7 rounded-full hover:bg-secondary flex items-center justify-center">
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>

          <div className="p-2 space-y-1">
            {/* Omstilling */}
            <button
              onClick={() => handleTogglePanel('omstilling')}
              className={cn("w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-secondary transition-colors text-left", phoneActive && "bg-secondary")}
            >
              <div className={cn("w-9 h-9 rounded-full flex items-center justify-center shrink-0",
                phoneStatus === 'ringing' ? "bg-destructive/10" :
                phoneStatus === 'on-call' ? "bg-blue-500/10" :
                phoneStatus === 'consulting' ? "bg-purple-500/10" :
                "bg-emerald-500/10"
              )}>
                <Phone className={cn("w-4.5 h-4.5",
                  phoneStatus === 'ringing' ? "text-destructive" :
                  phoneStatus === 'on-call' ? "text-blue-500" :
                  phoneStatus === 'consulting' ? "text-purple-500" :
                  "text-emerald-500"
                )} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground">Omstilling</p>
                <p className="text-[10px] text-muted-foreground">
                  {phoneStatus === 'ringing' ? 'Indgående opkald!' :
                   phoneStatus === 'on-call' ? 'I opkald' :
                   phoneStatus === 'consulting' ? 'Konsultation' :
                   phoneStatus === 'ready' ? 'Klar' : 'Telefon'}
                </p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {getStateIndicator(states.omstilling, poppedOut.omstilling)}
                {phoneRinging && states.omstilling !== 'open' && (
                  <span className="w-2.5 h-2.5 rounded-full bg-destructive animate-pulse" />
                )}
              </div>
            </button>

            {/* AI Assistant */}
            <button
              onClick={() => handleTogglePanel('ai_assistent')}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-secondary transition-colors text-left"
            >
              <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <Bot className="w-4.5 h-4.5 text-primary" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground">AI Assistent</p>
                <p className="text-[10px] text-muted-foreground">Spørg om sager, tilbud, fakturaer</p>
              </div>
              {getStateIndicator(states.ai_assistent, poppedOut.ai_assistent)}
            </button>

            {/* Internal Chat */}
            <button
              onClick={() => handleTogglePanel('intern_chat')}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-secondary transition-colors text-left"
            >
              <div className="w-9 h-9 rounded-full bg-blue-500/10 flex items-center justify-center shrink-0">
                <MessageSquare className="w-4.5 h-4.5 text-blue-500" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground">Intern Chat</p>
                <p className="text-[10px] text-muted-foreground">Skriv til kollegaer</p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {getStateIndicator(states.intern_chat, poppedOut.intern_chat)}
                {internalChatUnread > 0 && (
                  <span className="bg-primary text-primary-foreground text-xs px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                    {internalChatUnread}
                  </span>
                )}
              </div>
            </button>

            {/* Active conversations */}
            {conversations.length > 0 && (
              <>
                <div className="px-3 pt-2 pb-1">
                  <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Aktive samtaler</span>
                </div>
                {conversations.map(conv => (
                  <div key={conv.customerId} className="group relative flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-secondary transition-colors">
                    <button
                      onClick={() => handleOpenConversation(conv)}
                      className="flex items-center gap-3 flex-1 min-w-0 text-left"
                    >
                      <div className="w-9 h-9 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center shrink-0">
                        <span className="text-xs font-bold text-emerald-700 dark:text-emerald-300">
                          {conv.customerName?.[0]?.toUpperCase() || 'K'}
                        </span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-foreground truncate">{conv.customerName}</p>
                        <p className="text-[10px] text-muted-foreground truncate">{conv.projectTitle || 'Sag'}</p>
                      </div>
                      <MessageCircle className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); chatCtx?.removeConversation(conv.customerId); }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                      title="Luk samtale"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );

  function renderPoppedPanels() {
    const keys: PanelKey[] = ['omstilling', 'ai_assistent', 'intern_chat'];
    return (
      <>
        {keys.map(key => {
          if (!poppedOut[key] || states[key] === 'closed') return null;
          return (
            <FloatingPanel
              key={key}
              title={panelTitles[key]}
              icon={panelIcons[key]}
              state={states[key]}
              position={positions[key]}
              zIndex={zIndices[key]}
              onClose={() => setPanelState(key, 'closed')}
              onDock={() => handleDock(key)}
              onPositionChange={(pos) => updatePosition(key, pos)}
              onFocus={() => bringToFront(key)}
            >
              {renderFloatingContent(key)}
            </FloatingPanel>
          );
        })}
      </>
    );
  }
}

function useChatContextSafe() {
  try {
    return useChatContext();
  } catch {
    return null;
  }
}
